  import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';

  const CaseSelectionContext = createContext();

  export const CaseSelectionProvider = ({ children }) => {
    // Core selection states
    const [selectedCaseType, setSelectedCaseType] = useState('fetched');
    const [selectedCourtType, setSelectedCourtType] = useState('HighCourt');
    const [selectedAssignedUser, setSelectedAssignedUser] = useState('');
    const [selectedLabel, setSelectedLabel] = useState('');
    
    const [selectedCaseCard, setSelectedCaseCard] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    
    // Filters state
    const [filters, setFilters] = useState({
      status: "",
      type: "all",
      filingDateFrom: "",
      filingDateTo: "",
      registrationDateFrom: "",
      registrationDateTo: "",
      firstHearingDateFrom: "",
      firstHearingDateTo: "",
      decisionDateFrom: "",
      decisionDateTo: "",
    });
    
    // Pagination state
    const [pagination, setPagination] = useState({
      page: 0,
      limit: 15,
      hasMore: true,
      totalCases: 0
    });
    
    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    
    // Store filtered cases for each tab combination
    const [filteredCasesByTab, setFilteredCasesByTab] = useState({});
    
    const prevTabKeyRef = useRef('');
    const lastUpdateTimeRef = useRef(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const triggerCaseRefresh = useCallback(() => {
        setRefreshKey(prevKey => prevKey + 1);
      }, []);
    
    
    const currentTabKey = useMemo(() => {
      const tabKey = `${selectedCaseType}_${selectedCourtType}`;
      if (prevTabKeyRef.current !== tabKey) {
        prevTabKeyRef.current = tabKey;
      }
      return tabKey;
    }, [selectedCaseType, selectedCourtType]);
    
    const filteredCases = useMemo(() => {
      return filteredCasesByTab[currentTabKey] || [];
    }, [filteredCasesByTab, currentTabKey]);
    
const setFilteredCases = useCallback((cases) => {
  const now = Date.now();
  
  // Throttling to prevent excessive updates
  if (now - lastUpdateTimeRef.current < 50) {
    return;
  }
  lastUpdateTimeRef.current = now;

  setFilteredCasesByTab(prev => {
    const currentCases = prev[currentTabKey];
    
    // Always update if cases is empty array (important for clearing)
    if (Array.isArray(cases) && cases.length === 0) {
      return {
        ...prev,
        [currentTabKey]: []
      };
    }
    
    // Check if cases are actually different
    if (currentCases === cases) {
      return prev;
    }
    
    if (currentCases && currentCases.length === cases.length) {
      let hasChanged = false;
      for (let i = 0; i < cases.length; i++) {
        if (currentCases[i] !== cases[i]) {
          hasChanged = true;
          break;
        }
      }
      if (!hasChanged) {
        return prev;
      }
    }
    
    return {
      ...prev,
      [currentTabKey]: Array.isArray(cases) ? [...cases] : []
    };
  });
  
  // Set loading to false after cases are updated
  setIsLoading(false);
}, [currentTabKey]);
    
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    
    // Only add pagination parameters if limit is not "all"
    if (pagination.limit !== "all") {
      params.set('page', pagination.page.toString());
      params.set('limit', pagination.limit.toString());
    }
    
    if (searchTerm && searchTerm.trim()) {
      params.set('search', searchTerm.trim());
    }
    
    // Use selectedCaseCard instead of selectedCard
    if (selectedCaseCard) {
      params.set('onClick', selectedCaseCard);
    }
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.trim()) {
        params.set(key, value.trim());
      }
    });
    
    if (selectedAssignedUser && selectedAssignedUser !== '') {
      params.set('assignedIds', selectedAssignedUser);
    }
    
    return params.toString();
  }, [pagination, searchTerm, selectedCaseCard, filters, selectedAssignedUser]);
    
    // Reset pagination when filters change
    const resetPagination = useCallback(() => {
      setPagination(prev => ({
        ...prev,
        page: 0,
        hasMore: true
      }));
    }, []);
    
const clearAllFilters = useCallback(() => {
  
  setIsLoading(true); // Start loading
  
  setFilters({
    status: "",
    type: "all",
    filingDateFrom: "",
    filingDateTo: "",
    registrationDateFrom: "",
    registrationDateTo: "",
    firstHearingDateFrom: "",
    firstHearingDateTo: "",
    decisionDateFrom: "",
    decisionDateTo: "",
  });
  
  setSearchTerm('');
  setSelectedCaseCard(null);
  setSelectedAssignedUser('');
  setSelectedLabel('');
  resetPagination();
  
  setFilteredCasesByTab(prev => ({
    ...prev,
    [currentTabKey]: []
  }));
}, [resetPagination, currentTabKey]);
    
// const handleCaseCardClick = useCallback((cardType) => {
//   setIsLoading(true); // Start loading
//   setSelectedCaseCard(prevCard => prevCard === cardType ? null : cardType);
//   resetPagination();
// }, [resetPagination]);

// const handleCaseCardClick = useCallback((cardType) => {
//   setIsLoading(true);
//   if (cardType === "" || cardType === "total") {
//     setSelectedCaseCard(null); // Clear selection for total cases
//   } else {
//     setSelectedCaseCard(prevCard => prevCard === cardType ? null : cardType);
//   }
//   resetPagination();
// }, [resetPagination]);

const handleCaseCardClick = useCallback((cardType) => {
  setIsLoading(true); // Start loading

  // If the "Total Cases" card is clicked, reset EVERYTHING.
  if (cardType === "" || cardType === "total" || cardType === null) {
    clearAllFilters(); 
  } else {
    // For any other card, just toggle its state as before.
    setSelectedCaseCard(prevCard => prevCard === cardType ? null : cardType);
    resetPagination();
  }
}, [clearAllFilters, resetPagination]); // Add clearAllFilters to dependency array
    
    // FIXED: Simplified setters that don't prevent updates
    const setFiltersStable = useCallback((newFilters) => {
      setFilters(newFilters);
      resetPagination();
    }, [resetPagination]);
    
    const setSearchTermStable = useCallback((term) => {
      if (term !== searchTerm) {
        setSearchTerm(term);
        resetPagination();
      }
    }, [searchTerm, resetPagination]);
    
    const setSelectedCaseTypeStable = useCallback((caseType) => {
      if (caseType !== selectedCaseType) {
        setSelectedCaseType(caseType);
        resetPagination();
      }
    }, [selectedCaseType, resetPagination]);
    
    const setSelectedCourtTypeStable = useCallback((courtType) => {
      if (courtType !== selectedCourtType) {
        setSelectedCourtType(courtType);
        resetPagination();
      }
    }, [selectedCourtType, resetPagination]);
    
    // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return selectedCaseCard !== null ||
          searchTerm.trim() !== '' ||
          selectedAssignedUser !== '' ||
          selectedLabel !== '' ||
          Object.values(filters).some(value => value && value !== 'all' && value.trim() !== '');
  }, [selectedCaseCard, searchTerm, selectedAssignedUser, selectedLabel, filters]);
    
    // Update pagination info
    const updatePaginationInfo = useCallback((totalCases, hasMore) => {
      setPagination(prev => ({
        ...prev,
        totalCases,
        hasMore
      }));
    }, []);
    
    // Load more cases (for pagination)
    const loadMore = useCallback(() => {
      if (pagination.hasMore) {
        setPagination(prev => ({
          ...prev,
          page: prev.page + 1
        }));
      }
    }, [pagination.hasMore]);
    
    const contextValue = useMemo(() => ({
      // Core selections
      selectedCaseType,
      setSelectedCaseType: setSelectedCaseTypeStable,
      selectedCourtType,
      setSelectedCourtType: setSelectedCourtTypeStable,
      selectedAssignedUser,
      setSelectedAssignedUser,
      selectedLabel,
      setSelectedLabel,
      isLoading,
  setIsLoading,
      
      // Card selection
      selectedCaseCard,
      setSelectedCaseCard,
      handleCaseCardClick,

      // Filters
      filters,
      setFilters: setFiltersStable,
      
      // Search
      searchTerm,
      setSearchTerm: setSearchTermStable,
      
      // Pagination
      pagination,
      setPagination,
      resetPagination,
      updatePaginationInfo,
      loadMore,
      
      // Filtered cases
      filteredCases,
      setFilteredCases,

      refreshKey,
      triggerCaseRefresh,
      
      // Utilities
      buildQueryParams,
      clearAllFilters,
      hasActiveFilters,
      currentTabKey,
    }), [
      selectedCaseType,
      setSelectedCaseTypeStable,
      selectedCourtType,
      setSelectedCourtTypeStable,
      selectedAssignedUser,
      selectedLabel,
      selectedCaseCard,
      handleCaseCardClick,
      filters,
      setFiltersStable,
      searchTerm,
      setSearchTermStable,
      pagination,
      resetPagination,
      updatePaginationInfo,
      loadMore,
      filteredCases,
      setFilteredCases,
      buildQueryParams,
      clearAllFilters,
      hasActiveFilters,
      currentTabKey,
      isLoading,
      refreshKey,
      triggerCaseRefresh
    ]);
    
    return (
      <CaseSelectionContext.Provider value={contextValue}>
        {children}
      </CaseSelectionContext.Provider>
    );
  };

  export const useCaseSelection = () => {
    const context = useContext(CaseSelectionContext);
    if (!context) {
      throw new Error('useCaseSelection must be used within a CaseSelectionProvider');
    }
    return context;
  };